import { RealtimeMessage, CreateWebsocketClientRequest } from '@inplay/models';

export type MessageHandler = (data: RealtimeMessage) => void;
export type EventHandler = () => void;
export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

const MAX_RECONNECT_DELAY_MS = 30000;
const PING_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

export class WebSocketService {
  private socket: WebSocket | null = null;
  private readonly url: string;

  private onOpenHandler?: EventHandler;
  private onCloseHandler?: EventHandler;
  private onStatusChangeHandler?: (status: ConnectionStatus) => void;

  private readonly onMessageHandlers: Set<MessageHandler> = new Set();

  private pingTimer: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private lastPingTime = 0;

  constructor(url: string) {
    this.url = url;
  }

  private startPing(): void {
    this.stopPing();
    this.lastPingTime = Date.now();
    this.pingTimer = globalThis.setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ action: 'ping' }));
        this.lastPingTime = Date.now();
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose) return;
    this.onStatusChangeHandler?.('reconnecting');
    const delay = Math.min(
      1000 * 2 ** this.reconnectAttempts,
      MAX_RECONNECT_DELAY_MS,
    );
    this.reconnectTimer = globalThis.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  public connect(): void {
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      return;
    }

    this.intentionalClose = false;
    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.startPing();
      this.onStatusChangeHandler?.('connected');
      this.onOpenHandler?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.action === 'pong' || data?.type === 'Heartbeat') return;
        this.onMessageHandlers.forEach((handler) => handler(data));
      } catch (error) {
        console.error(
          '[WebSocketService] Failed to parse message:',
          event.data,
          error,
        );
      }
    };

    this.socket.onclose = (event: CloseEvent) => {
      this.stopPing();
      this.onCloseHandler?.();
      if (event.code === 1000) {
        this.onStatusChangeHandler?.('disconnected');
      } else {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error('[WebSocketService] error:', error);
    };
  }

  public sendMessage(message: CreateWebsocketClientRequest): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  public onOpen(handler: EventHandler): void {
    this.onOpenHandler = handler;
  }

  public onMessage(handler: MessageHandler): () => void {
    this.onMessageHandlers.add(handler);
    return () => {
      this.onMessageHandlers.delete(handler);
    };
  }

  public onClose(handler: EventHandler): void {
    this.onCloseHandler = handler;
  }

  public onStatusChange(handler: (status: ConnectionStatus) => void): void {
    this.onStatusChangeHandler = handler;
  }

  public getReadyState(): number {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }

  public isConnectionStale(): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return true;
    // If last ping was more than 9 minutes ago, connection is likely dead
    return Date.now() - this.lastPingTime > 9 * 60 * 1000;
  }

  public reconnect(): void {
    this.stopPing();
    this.socket?.close();
    this.connect();
  }

  public disconnect(): void {
    this.intentionalClose = true;
    this.stopPing();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close(1000, 'navigating away');
    this.onStatusChangeHandler?.('disconnected');
  }
}
